/**
 * Course-section vocabulary, in the words a coordinator actually uses.
 *
 * NOTE ON WHAT IS *NOT* HERE. There is no hours arithmetic and no census
 * calculation in this file, deliberately. Both come from the server, which gets
 * the ÷10 / ÷4 rule from Postgres. A copy of that maths in the browser would be
 * a second version of a compliance rule, and the moment the two disagreed the
 * coordinator would be looking at a census date the record does not have.
 */

/** Printed on the Official Attendance Record (H8 §5). */
export const DELIVERY_METHODS = [
  { value: 'TR', label: 'TR — Traditional (all seat time)' },
  { value: 'IN', label: 'IN — Internet / online' },
  { value: 'BL', label: 'BL — Blended' },
  { value: 'HY', label: 'HY — Hybrid' },
  { value: 'HF', label: 'HF — HyFlex' },
];

/** Decides how census and membership are computed (H8 §4). */
export const SCHEDULE_TYPES = [
  {
    value: 'regular',
    label: 'Regularly scheduled',
    help: 'Meets on set dates. Census falls at scheduled hours ÷ 10, counted from the start.',
  },
  {
    value: 'non_regular',
    label: 'Non-regularly scheduled',
    help: 'No census. Funded on actual contact hours — 60 minutes counts as one hour.',
  },
  {
    value: 'independent',
    label: 'Independently scheduled (asynchronous)',
    help: 'Self-paced. Census falls at scheduled hours ÷ 4, counted back from the end date.',
  },
  {
    value: 'hyflex',
    label: 'HyFlex',
    help: 'Census is a window set by policy (CC23-026), not a computed date. Synchronous and asynchronous membership report separately.',
  },
];

export const STATUS_LABELS = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
  cancelled: 'Cancelled',
};

/** A reporting period key ("fall_2026") as a coordinator would say it. */
export function reportingPeriodLabel(key) {
  if (!key) return null;
  const [season, year] = key.split('_');
  if (!year) return key;
  return `${season.charAt(0).toUpperCase()}${season.slice(1)} ${year}`;
}

/** A blank section, ready for the builder form. */
export function emptySection(institutionId = '') {
  return {
    institution_id: institutionId,
    title: '',
    topic: '',
    ce_hours: '',
    passing_threshold: '80',
    ccl_course_code: '',
    course_prefix: '',
    course_number: '',
    section_number: '',
    official_ccl_title: '',
    state_approved_hours: '',
    delivery_method: '',
    section_schedule_type: '',
    meeting_schedule: [],
    course_id: '',
  };
}

/**
 * Form state to an API payload.
 *
 * Empty text boxes become null rather than "", because "" is a value a database
 * happily stores and a report happily prints as a blank CCL code.
 */
export function toPayload(form) {
  const text = (v) => {
    const trimmed = (v ?? '').trim();
    return trimmed === '' ? null : trimmed;
  };
  const number = (v) => {
    const trimmed = (v ?? '').toString().trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  };

  return {
    title: (form.title ?? '').trim(),
    topic: text(form.topic),
    ce_hours: number(form.ce_hours) ?? 0,
    passing_threshold: number(form.passing_threshold) ?? 80,
    ccl_course_code: text(form.ccl_course_code),
    course_prefix: text(form.course_prefix),
    course_number: text(form.course_number),
    section_number: text(form.section_number),
    official_ccl_title: text(form.official_ccl_title),
    state_approved_hours: number(form.state_approved_hours),
    delivery_method: text(form.delivery_method),
    section_schedule_type: text(form.section_schedule_type),
    course_id: text(form.course_id),
    meeting_schedule: (form.meeting_schedule ?? []).map((m) => ({
      date: m.date,
      start: m.start,
      end: m.end,
      location: (m.location ?? '').trim(),
    })),
  };
}

/** API row to form state — the inverse, so a saved draft reopens as it was left. */
export function toForm(section) {
  const str = (v) => (v === null || v === undefined ? '' : String(v));
  return {
    institution_id: section.institution_id ?? '',
    title: str(section.title),
    topic: str(section.topic),
    ce_hours: str(section.ce_hours),
    passing_threshold: str(section.passing_threshold),
    ccl_course_code: str(section.ccl_course_code),
    course_prefix: str(section.course_prefix),
    course_number: str(section.course_number),
    section_number: str(section.section_number),
    official_ccl_title: str(section.official_ccl_title),
    state_approved_hours: str(section.state_approved_hours),
    delivery_method: str(section.delivery_method),
    section_schedule_type: str(section.section_schedule_type),
    meeting_schedule: Array.isArray(section.meeting_schedule) ? section.meeting_schedule : [],
    course_id: str(section.course_id),
  };
}

/** "EMS 3020-01" from the parts, for a list row. */
export function sectionCode(section) {
  const parts = [section.course_prefix, section.course_number].filter(Boolean).join(' ');
  if (!parts) return section.ccl_course_code ?? null;
  return section.section_number ? `${parts}-${section.section_number}` : parts;
}
